import React from "react";
import { useAuth } from "@clerk/nextjs";
import { ASK_PAPER_CREDITS_PER_MONTH_FREE_USER } from "constants/config";
import { SubscriptionPlan } from "enums/subscription-plans";
import { SubscriptionPlanIcon } from "features/Pricing/utils";
import useCredits from "features/User/hooks/useCredits";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import CardTooltip from "../CardTooltip";

/**
 * @component AskPaperTooltip
 * @description Tooltip for the "beta" tag in the Ask Paper thread heading
 */

interface AskPaperTooltipProps {
  onClickUpgradeToPremium?: () => void;
}

function AskPaperTooltip({ onClickUpgradeToPremium }: AskPaperTooltipProps) {
  const [tooltipLabels, generalLabels, creditsLabels] = useLabels(
    "tooltips.ask-paper",
    "general",
    "tooltips.credits"
  );

  const { isSignedIn, isLoaded } = useAuth();
  const notSignedIn = isLoaded && isSignedIn === false;

  const { isPremium, askPaperCreditsRemaining, creditRefreshDate } =
    useCredits();

  const handleClickUpgradePremium = async () => {
    if (onClickUpgradeToPremium) {
      onClickUpgradeToPremium();
    }
  };

  const showCreditsRemaining = !isPremium && !notSignedIn;

  return (
    <CardTooltip>
      <CardTooltip.Header
        title={tooltipLabels["ask-paper"]}
        iconComponent={
          <img
            alt="ask paper"
            src="/icons/ask-paper.svg"
            width="24px"
            height="24px"
          />
        }
        hint={
          showCreditsRemaining &&
          creditsLabels["credits-left"].replace(
            "{CREDITS_REMAINING}",
            askPaperCreditsRemaining
          )
        }
        isBeta
      />
      <CardTooltip.Description content={tooltipLabels["description"]} />
      <CardTooltip.InfoSection>
        <div className="flex flex-col gap-1 px-3 py-2 bg-chart-red-faint rounded-xl">
          <div className="flex items-center gap-2 sm-md">
            <i className="text-xl icon-info" />
            <span>{generalLabels["keep-in-mind"]}</span>
          </div>
          <div className="sm-normal">{tooltipLabels["warning"]}</div>
        </div>
      </CardTooltip.InfoSection>
      {showCreditsRemaining && (
        <CardTooltip.Hint
          content={pluralize(creditsLabels["ask-paper-messages-left"], {
            CREDITS_REMAINING: askPaperCreditsRemaining,
            MAX_CREDIT: ASK_PAPER_CREDITS_PER_MONTH_FREE_USER,
            RENEW_DATE: creditRefreshDate,
          })}
        />
      )}
      {showCreditsRemaining && (
        <CardTooltip.Button
          icon={<SubscriptionPlanIcon plan={SubscriptionPlan.PREMIUM} />}
          label={creditsLabels["upgrade-to-premium"]}
          onClick={handleClickUpgradePremium}
        />
      )}
    </CardTooltip>
  );
}

export default AskPaperTooltip;
